import { getDashboard } from "api/dashboard";
import { devicePage } from "api/device";
import { DashboardReqBody, Section } from "api/types/dashboard";
import { DevicePageRespData } from "api/types/device";
import { t } from "i18next";
import { deviceConfiguration } from "models/devices/configuration";
import { ModelFilterDropdown } from "models/dropdowns/modelFilter";
import defineDpTablecolumns, {
  DevicePageTableModel,
} from "models/tables/devicePage";
import { notifySimple } from "providers/toast";
import { useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { accountSiteList, issuesList } from "store/appSelectors";
import { setIssuesList } from "store/appSlice";
import { getIssuesListFilters } from "store/page/pageSelectors";
import { DevicePageFilters, setIssuesPageFilters } from "store/page/pageSlice";
import { deepSearch } from "util/hooks";

import Combobox from "widgets/shared/inputs/combobox";
import { SearchInput } from "widgets/shared/inputs/search";
import Table from "widgets/controls/tables/genericTable";

const Issues = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const columns = defineDpTablecolumns(
    (info) => {
      const row = info.row.original;
      handleRowClick(row);
    },
    (info) => {
      const row = info.row.original;
    },
    (info) => {},
    (info) => {
      const row = info.row.original;
    },
    { hideActions: true }
  );
  const userInfo = useSelector(accountSiteList, shallowEqual);

  const issuesDropdown = useSelector(issuesList, shallowEqual);

  // Get initial filters from store once
  const initialFilters = useSelector(getIssuesListFilters);

  // Local state for filters - initialized once from Redux
  const [filters, setFilters] = useState<DevicePageFilters>(
    () => initialFilters
  );

  const [dataSource, setDataSource] = useState<DevicePageRespData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    const {
      paginationState,
      sortingState,
      classId,
      model,
      status,
      installed,
      snrs,
      search,
    } = filters;

    let lsnrs = undefined;
    if (snrs && snrs.length > 0) {
      lsnrs = snrs;
    } else if (issuesDropdown.allDevices.length > 0) {
      lsnrs = issuesDropdown.allDevices;
    }

    if (lsnrs) {
      setIsLoading(true);
      const {
        data,
        error,
        isLoading,
        status: status2,
      } = await devicePage({
        search: search,
        pageIndex: paginationState.pageIndex + 1,
        pageSize: paginationState.pageSize,
        sort: sortingState[0].id,
        desc: sortingState[0].desc,
        classId: classId,
        models: model,
        status: status,
        installed: installed,
        snrs: lsnrs,
      });
      setIsLoading(isLoading);
      if (!error && status2 && status2 === 200) {
        setDataSource(data.devices || []);

        setFilters((prevFilters) => ({
          ...prevFilters,
          paginationState: {
            pageIndex: data.currentPage - 1,
            pageSize: paginationState.pageSize,
          },
          sortingState: sortingState,
          totCount: data.totalCount,
          pageCount: data.totalPages,
        }));

        dispatch(
          setIssuesPageFilters({
            paginationState: { pageIndex: 0, pageSize: 10 },
            sortingState: [{ desc: true, id: "name" }],
            totCount: 0,
            pageCount: 0,
            classId: classId,
            model: model,
            status: status,
            installed: installed,
            snrs: lsnrs,
            search: search,
          })
        );
      }
    }
  }, [
    filters.paginationState.pageIndex,
    filters.paginationState.pageSize,
    filters.sortingState,
    filters.classId,
    filters.model,
    filters.status,
    filters.installed,
    filters.search,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRowClick = (row: DevicePageTableModel) => {
    if (deviceConfiguration.canDeviceRoute(row.model)) {
      navigate(`/admin/devices/device?snr=${row.snr}`);
    } else {
      notifySimple(t("device.not.supported"), "error");
    }
  };

  const getDashboardData = async () => {
    const sections: DashboardReqBody = {
      sections: [
        Section.DEVICES_DOWN,
        Section.ZERO_COUNT,
        Section.LEAKING,
        Section.WEAK_SIGNAL,
        Section.LOW_BATTERY,
        Section.WATER_BREAKDOWN,
        Section.ELECTRICITY_BREAKDOWN,
        Section.TEMPERATURE_BREAKDOWN,
        Section.LEVEL_BREAKDOWN,
      ],
    };

    const { data, status } = await getDashboard(sections);

    if (status === 200) {
      const deepData = deepSearch(data);
      dispatch(setIssuesList(deepData));
    }
  };

  useEffect(() => {
    getDashboardData();
  }, []);

  return (
    <div>
      <Table
        filterbar={
          <div className="flex h-full w-full flex-col gap-y-3">
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-5">
              <div className="flex-1 lg:flex-grow-[1]">
                <Combobox
                  id="device-select-type-issue"
                  placeholder={t("common.select.issue")}
                  variant="solid"
                  color="secondary"
                  value={undefined}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      snrs: item ? item : [],
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  items={issuesDropdown.dropdownOptions}
                  labelKey="label"
                  valueKey="value"
                  sizes="md"
                  rounded="rounded-xl"
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <Combobox
                  id="device-select-model"
                  placeholder={t("common.select.group")}
                  variant="solid"
                  color="secondary"
                  value={filters.model}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      model: item,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  items={ModelFilterDropdown}
                  labelKey="label"
                  valueKey="value"
                  sizes="md"
                  rounded="rounded-xl"
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <Combobox
                  filter
                  id="device-select-site"
                  placeholder={t("common.select.site")}
                  variant="solid"
                  color="secondary"
                  value={filters.classId}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      classId: item,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  items={userInfo}
                  labelKey="name"
                  valueKey="id"
                  sizes="md"
                  rounded="rounded-xl"
                />
              </div>

              <div className="flex-1 lg:flex-grow-[1]">
                <SearchInput
                  onChange={(filters) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      search: filters,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  type="text"
                  value={filters.search}
                  sizes="md"
                  rounded="rounded-xl"
                  color="secondary"
                />
              </div>
            </div>
          </div>
        }
        noDataLabel={t("device.issues.no.data")}
        loading={isLoading}
        onRowClick={handleRowClick}
        data={dataSource}
        columns={columns}
        pagination={filters.paginationState}
        paginationOptions={{
          onPaginationChange: (pagination) => {
            const page =
              typeof pagination === "function"
                ? pagination(filters.paginationState)
                : pagination;
            setFilters((prevFilters) => ({
              ...prevFilters,
              paginationState: page,
            }));
          },
          rowCount: filters.totCount,
          pageCount: filters.pageCount,
        }}
        sorting={filters.sortingState}
        onSortingChange={(updaterOrValue) => {
          const newSortingState =
            typeof updaterOrValue === "function"
              ? updaterOrValue(filters.sortingState)
              : updaterOrValue;
          if (newSortingState && newSortingState.length > 0) {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: newSortingState,
            }));
          } else {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: [{ id: "name", desc: false }],
            }));
          }
        }}
      />
    </div>
  );
};

export default Issues;
